import React from 'react';
import _ from 'lodash';
import classnames from 'classnames';
import { Dropdown, Menu } from 'antd';
import { TOOLBAR_TYPE } from '@/shared/ui';
import ToolbarArrowIcon from '@/shared/ui/icons/ECGViewer/ecg-toolbar-arrow-icon.svg';
import { GAIN_VALUES, HP_VALUES, LP_VALUES, SPEED_VALUES } from '@/shared/config';

type ECGToolButtonProps = {
  value: string | number;
  type: string;
  onChange: (value: any) => void;
  disabled: boolean;
};

/**
 * Renders a single ECG viewer toolbar control for speed, gain, high-pass or low-pass. Delegates entirely to an internal renderDropdown that builds the value menu for the given type and reports the picked value through onChange.
 *
 * @param props - Control props carrying type, value, disabled and onChange.
 * @returns The rendered toolbar dropdown control.
 */
const ECGToolButton = ({ value, type, onChange, disabled }: ECGToolButtonProps) => {
  // antd's MenuInfo.item is typed as the deprecated React.ReactInstance, which doesn't
  // expose `.props` — this reads the custom `value` field set on the rendered menu item.
  /**
   * Forwards a chosen ECG toolbar dropdown entry to the parent as a plain value. Reads the custom value field off the antd menu item instance and passes it to the onChange prop.
   *
   * @param item - The clicked antd menu item, whose props carry the toolbar value.
   * @returns Nothing.
   */
  const onClickItem = ({ item }: { item: any }) => {
    onChange(item.props.value);
  };

  /**
   * Builds the ECG toolbar dropdown for the configured setting and its current value. Switches on type to fill the menu from SPEED_VALUES, GAIN_VALUES, HP_VALUES or LP_VALUES, marks the entry matching value as selected, formats the button label with its unit (speed to one decimal, zero shown as No, 50 and 60 Hz labelled Notch), and wraps it in a click-triggered Dropdown.
   *
   * @returns The rendered dropdown with its formatted toolbar button.
   */
  const renderDropdown = () => {
    const items: any[] = [];
    let formatText;
    switch (type) {
      case TOOLBAR_TYPE.SPEED: {
        _.forEach(SPEED_VALUES, (x, i) =>
          items.push({
            key: `speed-${i}`,
            label: <div className={classnames(x === value ? 'selected' : '')}>{`${x} mm/s`}</div>,
            value: x,
          }),
        );
        formatText = (
          <button className="toolbar-text">
            <div className="type">{TOOLBAR_TYPE.SPEED}:</div>
            <div className="value">
              {(Number(value) * 10) % 10 === 0 ? value : Number(value).toFixed(1)}
            </div>
            <img src={ToolbarArrowIcon} alt="arrow-icon" />
          </button>
        );
        break;
      }
      case TOOLBAR_TYPE.GAIN: {
        _.forEach(GAIN_VALUES, (x, i) =>
          items.push({
            key: `gain-${i}`,
            label: <div className={classnames(x === value ? 'selected' : '')}>{`${x} mm/mV`}</div>,
            value: x,
          }),
        );
        formatText = (
          <button className="toolbar-text">
            <div className="type">{TOOLBAR_TYPE.GAIN}:</div>
            <div className="value">{value}</div>
            <img src={ToolbarArrowIcon} alt="arrow-icon" />
          </button>
        );
        break;
      }
      case TOOLBAR_TYPE.HIGH_PASS: {
        _.forEach(HP_VALUES, (x, i) =>
          items.push({
            key: `hiP-${i}`,
            label: (
              <div className={classnames(x === value ? 'selected' : '')}>
                {x !== 0 ? `${x} Hz` : 'No'}
              </div>
            ),
            value: x,
          }),
        );
        formatText = (
          <button className="toolbar-text">
            <div className="type">{TOOLBAR_TYPE.HIGH_PASS}:</div>
            <div className="value">{value !== 0 ? value : 'No'}</div>
            <img src={ToolbarArrowIcon} alt="arrow-icon" />
          </button>
        );
        break;
      }
      case TOOLBAR_TYPE.LOW_PASS: {
        _.forEach(LP_VALUES, (x, i) =>
          items.push({
            key: `loP-${i}`,
            label: (
              <div className={classnames(x === value ? 'selected' : '')}>
                {x !== 0 ? (x !== 50 && x !== 60 ? `${x} Hz` : `${x} Hz (Notch)`) : 'No'}
              </div>
            ),
            value: x,
          }),
        );
        formatText = (
          <button className="toolbar-text">
            <div className="type">{TOOLBAR_TYPE.LOW_PASS}:</div>
            <div className="value">{value !== 0 ? value : 'No'}</div>
            <img src={ToolbarArrowIcon} alt="arrow-icon" />
          </button>
        );
        break;
      }
      default:
        break;
    }
    return (
      <Dropdown
        disabled={disabled}
        trigger={['click']}
        overlay={<Menu onClick={onClickItem} className="ecg-tool-menu" items={items} />}
      >
        {formatText}
      </Dropdown>
    );
  };
  return renderDropdown();
};

export default ECGToolButton;
